import { Rows2, Rows3, Rows4, type LucideIcon } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { Density } from "@/shared/hooks/useDensity";
import { Tooltip } from "@/shared/ui/tooltip";

interface DensityToggleProps {
  value: Density;
  onChange: (d: Density) => void;
}

const OPTIONS: { value: Density; icon: LucideIcon; labelKey: string }[] = [
  { value: "compact", icon: Rows4, labelKey: "density.compact" },
  { value: "default", icon: Rows3, labelKey: "density.default" },
  { value: "comfortable", icon: Rows2, labelKey: "density.comfortable" },
];

export function DensityToggle({ value, onChange }: DensityToggleProps): JSX.Element {
  const { t } = useTranslation("common");
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white p-[3px] shadow-[var(--sh-xs)]"
      role="group"
      aria-label={t("density.label")}
      data-testid="density-toggle"
    >
      {OPTIONS.map(({ value: v, icon: Icon, labelKey }) => (
        <Tooltip key={v} content={t(labelKey)}>
          <button
            type="button"
            onClick={() => onChange(v)}
            aria-pressed={value === v}
            aria-label={t(labelKey)}
            data-testid={`density-${v}`}
            className={[
              "grid h-[30px] w-8 place-items-center rounded-[7px] focus-ring transition-colors",
              value === v
                ? "bg-[var(--brand-50)] text-[var(--brand-500)]"
                : "text-[var(--text-subtle)] hover:bg-[var(--canvas)] hover:text-[var(--text-secondary)]",
            ].join(" ")}
          >
            <Icon className="h-4 w-4" aria-hidden />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
